import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import GeoLaunchService from 'services/GeoLaunchService';
import useActorName from 'shared/hooks/useActorName';
import {
  AdStatus,
  CheckResult,
  DestinationMode,
  GEOS,
  Geo,
  GeoInfo,
  PlanSummary,
  QueuedGeoBatch,
  ReviewAd,
  ReviewGroup,
  SourceAd,
  Winner,
  adsetsOf,
  formatNote,
  groupProblems,
  initialGroups,
  moveAd,
  toBatchRequest,
} from './geoLaunch';

function errorText(err: unknown): string {
  const anyErr = err as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return (
    anyErr?.response?.data?.error ||
    anyErr?.response?.data?.message ||
    anyErr?.message ||
    'Something went wrong.'
  );
}

const MODE_LABEL: Record<DestinationMode, string> = {
  new_adset: 'New ad set in a campaign',
  existing_adset: 'An existing ad set',
  new_campaign: 'New campaign (starts paused)',
};

const statusSuffix = (s: string) =>
  s === 'ACTIVE' ? '' : ` — ${s.toLowerCase().replace(/_/g, ' ')}`;

/** What a card is called in "Move to": the existing ad set's name, or the new one's. */
function cardName(group: ReviewGroup, info: GeoInfo | undefined): string {
  if (group.mode === 'existing_adset')
    return info?.adsets.find(a => a.id === group.adsetId)?.name || '';
  return group.adsetName ? `${group.adsetName} (new)` : '';
}

/** One ad inside a group: landing page, where it goes, and its copy. */
function AdRow({
  ad,
  source,
  winner,
  group,
  groups,
  info,
  planned,
  onChange,
  onMove,
}: {
  ad: ReviewAd;
  source: SourceAd | undefined;
  winner: Winner | undefined;
  group: ReviewGroup;
  groups: ReviewGroup[];
  info: GeoInfo | undefined;
  /** This ad in the last successful Check, if any. */
  planned: PlanSummary['ads'][number] | undefined;
  onChange: (next: ReviewAd) => void;
  onMove: (toKey: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const note = planned ? formatNote(planned, group.geo) : null;
  const suggested = winner?.geos[group.geo]?.suggestion?.link || '';
  const siblings = groups.filter(
    g => g.geo === group.geo && g.key !== group.key,
  );
  const edited = Object.keys(ad.texts).length;
  const cell = winner?.geos[group.geo];
  const possible =
    cell?.status === 'POSSIBLE'
      ? cell.copies.filter(c => c.strength === 'possible')
      : [];
  // The Check reads the geo account now; the matrix cell may be stale.
  const existing =
    planned?.existingCopies ??
    (cell?.status === 'LIVE' || cell?.status === 'PAUSED'
      ? cell.copies.filter(c => c.strength === 'certain')
      : []);

  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {winner?.thumbnailUrl ? (
          <Box
            component="img"
            src={winner.thumbnailUrl}
            alt=""
            sx={{
              width: 48,
              height: 48,
              objectFit: 'cover',
              borderRadius: 1,
              flexShrink: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'action.hover',
              borderRadius: 1,
              flexShrink: 0,
            }}
          />
        )}
        <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              title={winner?.adName}
            >
              {winner?.adName || ad.adId}
            </Typography>
            {source && (
              <Chip
                size="small"
                variant="outlined"
                label={
                  source.flexible
                    ? `flexible · ${source.flexibleImages + source.flexibleVideos} media`
                    : source.mediaKind
                }
                sx={{ height: 20 }}
              />
            )}
          </Stack>
          {note && (
            <Typography variant="caption" color="text.secondary">
              {note}
            </Typography>
          )}
          {existing.length > 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              Already in {group.geo}:{' '}
              {existing
                .map(
                  c =>
                    `"${c.name}" (${c.effectiveStatus.toLowerCase().replace(/_/g, ' ')})`,
                )
                .join(', ')}
              . Launching adds another copy.
            </Alert>
          )}
          {possible.length > 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              Possibly already in {group.geo}:{' '}
              {possible
                .map(
                  c =>
                    `"${c.name}" (${c.matchedBy === 'image' ? 'same image' : 'same name'})`,
                )
                .join(', ')}
              . Check it in Ads Manager before launching another copy.
            </Alert>
          )}
          <TextField
            size="small"
            fullWidth
            label={`${group.geo} landing page`}
            value={ad.link}
            onChange={e => onChange({ ...ad, link: e.target.value })}
            error={!ad.link.trim()}
            helperText={
              !ad.link.trim()
                ? `No ${group.geo} page found for this ad in live ${group.geo} ads — paste the ${group.geo} URL.`
                : ad.link === suggested
                  ? `Suggested from live ${group.geo} ads. US page: ${winner?.destinationUrl || '—'}`
                  : `US page: ${winner?.destinationUrl || '—'}`
            }
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              label="Ad set"
              value={group.key}
              onChange={e => onMove(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value={group.key}>This ad set</MenuItem>
              {siblings.map((g, i) => (
                <MenuItem key={g.key} value={g.key}>
                  Move to: {cardName(g, info) || `${group.geo} ad set ${i + 1}`}
                </MenuItem>
              ))}
              <MenuItem value="new">Move to a new ad set</MenuItem>
            </TextField>
            <Button
              size="small"
              onClick={() => setEditing(v => !v)}
              disabled={!source}
            >
              {editing
                ? 'Hide copy'
                : `Edit copy${edited ? ` (${edited} edited)` : ''}`}
            </Button>
          </Stack>
          <Collapse in={editing} unmountOnExit>
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              {source?.slots.map(slot => {
                const value = ad.texts[slot.key] ?? slot.text;
                const changed = slot.key in ad.texts;
                return (
                  <TextField
                    key={slot.key}
                    size="small"
                    fullWidth
                    multiline
                    maxRows={8}
                    label={slot.label}
                    value={value}
                    color={changed ? 'warning' : undefined}
                    focused={changed || undefined}
                    onChange={e => {
                      const texts = { ...ad.texts };
                      if (e.target.value === slot.text) delete texts[slot.key];
                      else texts[slot.key] = e.target.value;
                      onChange({ ...ad, texts });
                    }}
                    helperText={
                      changed
                        ? 'Edited for this geo — clear your change to use the US text.'
                        : undefined
                    }
                  />
                );
              })}
            </Stack>
          </Collapse>
        </Stack>
      </Stack>
    </Box>
  );
}

/** One ad set's destination: where in the geo account these ads go. */
function GroupCard({
  group,
  index,
  info,
  groups,
  sources,
  winners,
  result,
  onChange,
  onGroups,
}: {
  group: ReviewGroup;
  index: number;
  info: GeoInfo | undefined;
  groups: ReviewGroup[];
  sources: Map<string, SourceAd>;
  winners: Map<string, Winner>;
  result: CheckResult | undefined;
  onChange: (next: ReviewGroup) => void;
  onGroups: (next: ReviewGroup[]) => void;
}) {
  const campaigns = info?.campaigns || [];
  const problems = groupProblems(group, info);
  const set = (patch: Partial<ReviewGroup>) => onChange({ ...group, ...patch });
  // Carry the campaign already picked into the new mode where it still makes
  // sense, and never keep a settings ad set that belongs to another campaign.
  const switchMode = (mode: DestinationMode): Partial<ReviewGroup> => {
    const firstAdset = (campaignId: string) =>
      adsetsOf(info, campaignId)[0]?.id || '';
    if (mode === 'existing_adset') {
      const inCampaign = (id: string) =>
        adsetsOf(info, group.campaignId).some(s => s.id === id);
      const adsetId =
        [group.adsetId, group.templateAdsetId].find(
          id => id && inCampaign(id),
        ) || firstAdset(group.campaignId);
      return { mode, adsetId };
    }
    if (mode === 'new_campaign') {
      const source =
        campaigns.find(c => c.id === group.campaignId && c.dailyBudget)?.id ||
        '';
      return {
        mode,
        sourceCampaignId: source,
        templateAdsetId: firstAdset(source),
      };
    }
    // New ad set: copy settings from the ad set the card was launching into,
    // if it was one; else keep the settings source if it is still in the
    // campaign.
    const inCampaign = (id: string) =>
      adsetsOf(info, group.campaignId).some(s => s.id === id);
    const preferred =
      group.mode === 'existing_adset'
        ? [group.adsetId, group.templateAdsetId]
        : [group.templateAdsetId, group.adsetId];
    return {
      mode,
      templateAdsetId:
        preferred.find(id => id && inCampaign(id)) ||
        firstAdset(group.campaignId),
    };
  };
  const campaignForSettings =
    group.mode === 'new_campaign' ? group.sourceCampaignId : group.campaignId;
  const settingsAdsets = adsetsOf(info, campaignForSettings);
  const existingCampaign =
    group.mode === 'existing_adset' ? group.campaignId : '';

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={group.geo} color="primary" />
          <Typography variant="subtitle2">
            Ad set {index + 1} · {group.ads.length} ad
            {group.ads.length === 1 ? '' : 's'}
          </Typography>
        </Stack>

        <TextField
          select
          size="small"
          label="Launch into"
          value={group.mode}
          onChange={e => set(switchMode(e.target.value as DestinationMode))}
        >
          {(Object.keys(MODE_LABEL) as DestinationMode[]).map(m => (
            <MenuItem key={m} value={m}>
              {MODE_LABEL[m]}
            </MenuItem>
          ))}
        </TextField>

        {group.mode === 'new_adset' && (
          <>
            <TextField
              select
              size="small"
              label="Campaign"
              value={group.campaignId}
              onChange={e =>
                set({
                  campaignId: e.target.value,
                  templateAdsetId: adsetsOf(info, e.target.value)[0]?.id || '',
                })
              }
            >
              {campaigns.map(c => (
                <MenuItem key={c.id} value={c.id} disabled={!c.dailyBudget}>
                  {c.name}
                  {statusSuffix(c.effectiveStatus)}
                  {c.dailyBudget ? '' : ' — ad set budgets, not supported'}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}

        {group.mode === 'existing_adset' && (
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Campaign"
              value={existingCampaign}
              onChange={e =>
                set({
                  campaignId: e.target.value,
                  adsetId: adsetsOf(info, e.target.value)[0]?.id || '',
                })
              }
              sx={{ flex: 1 }}
            >
              {campaigns.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                  {statusSuffix(c.effectiveStatus)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Ad set"
              value={group.adsetId}
              onChange={e => set({ adsetId: e.target.value })}
              sx={{ flex: 1 }}
              disabled={!existingCampaign}
            >
              {adsetsOf(info, existingCampaign).map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                  {statusSuffix(s.effectiveStatus)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        )}

        {group.mode === 'new_campaign' && (
          <>
            <Alert severity="info" sx={{ py: 0 }}>
              A copy of the campaign you pick, with your name and budget. It is
              created <strong>paused</strong> — switch it on in Ads Manager when
              you are ready.
            </Alert>
            <TextField
              select
              size="small"
              label="Campaign to copy"
              value={group.sourceCampaignId}
              onChange={e =>
                set({
                  sourceCampaignId: e.target.value,
                  templateAdsetId: adsetsOf(info, e.target.value)[0]?.id || '',
                })
              }
            >
              {campaigns
                .filter(c => c.dailyBudget)
                .map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                    {statusSuffix(c.effectiveStatus)}
                  </MenuItem>
                ))}
            </TextField>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label="New campaign name"
                value={group.campaignName}
                onChange={e => set({ campaignName: e.target.value })}
                sx={{ flex: 2 }}
              />
              <TextField
                size="small"
                label="Daily budget"
                type="number"
                value={group.dailyBudget}
                onChange={e => set({ dailyBudget: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {info?.currency}
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1 }}
              />
            </Stack>
          </>
        )}

        {group.mode !== 'existing_adset' && (
          <Stack direction="row" spacing={1}>
            <TextField
              select
              size="small"
              label="Copy audience & bid settings from"
              value={group.templateAdsetId}
              onChange={e => set({ templateAdsetId: e.target.value })}
              disabled={!campaignForSettings}
              sx={{ flex: 1 }}
            >
              {settingsAdsets.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                  {statusSuffix(s.effectiveStatus)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="New ad set name"
              value={group.adsetName}
              onChange={e => set({ adsetName: e.target.value })}
              sx={{ flex: 1 }}
            />
          </Stack>
        )}

        {problems.length > 0 && (
          <Alert severity="warning" sx={{ py: 0 }}>
            {problems.join(' ')}
          </Alert>
        )}
        {result && !result.ok && <Alert severity="error">{result.error}</Alert>}
        {result && result.ok && (
          <Alert severity="success" sx={{ py: 0 }}>
            Checked: {result.plan.ads.length} ad
            {result.plan.ads.length === 1 ? '' : 's'} →{' '}
            {result.plan.newCampaign
              ? `new paused campaign "${result.plan.newCampaign.name}"`
              : `"${result.plan.campaign?.name}"`}
            {result.plan.mode === 'existing_adset'
              ? `, ad set "${result.plan.adsetName}"`
              : `, new ad set "${result.plan.adsetName}" (settings from "${result.plan.settingsFrom}")`}
            . Tracking: <code>{result.plan.urlTags}</code>
          </Alert>
        )}

        <Divider />
        {group.ads.map(ad => (
          <AdRow
            key={ad.adId}
            ad={ad}
            source={sources.get(ad.adId)}
            winner={winners.get(ad.adId)}
            group={group}
            groups={groups}
            info={info}
            planned={
              result?.ok
                ? result.plan.ads.find(p => p.sourceAdId === ad.adId)
                : undefined
            }
            onChange={next =>
              onChange({
                ...group,
                ads: group.ads.map(a => (a.adId === ad.adId ? next : a)),
              })
            }
            onMove={toKey =>
              onGroups(moveAd(groups, group.key, ad.adId, toKey))
            }
          />
        ))}
      </Stack>
    </Paper>
  );
}

/**
 * Review before launch: every ticked cell, grouped into ad sets per geo, with
 * the destination, landing page and copy all editable. "Check" asks the API to
 * validate everything against the live geo accounts without creating anything;
 * "Launch" does the same check and then queues the batches.
 */
export default function GeoReviewDrawer({
  open,
  onClose,
  cells,
  winners,
  geos,
  service,
  onLaunched,
}: {
  open: boolean;
  onClose: () => void;
  cells: string[];
  winners: Map<string, Winner>;
  geos: Map<Geo, GeoInfo>;
  service: GeoLaunchService;
  onLaunched: (batches: QueuedGeoBatch[]) => void;
}) {
  const [groups, setGroups] = useState<ReviewGroup[]>(() =>
    initialGroups(cells, winners),
  );
  const [sources, setSources] = useState<Map<string, SourceAd>>(new Map());
  const [sourcesError, setSourcesError] = useState('');
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [launchActive, setLaunchActive] = useState(true);
  const [actor] = useActorName();
  const [checking, setChecking] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [results, setResults] = useState<Record<string, CheckResult>>({});
  const [error, setError] = useState('');

  const adIds = useMemo(
    () => Array.from(new Set(cells.map(c => c.split('|')[0]))),
    [cells],
  );
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSourcesLoading(true);
      try {
        const loaded: SourceAd[] = [];
        for (let i = 0; i < adIds.length; i += 50) {
          // eslint-disable-next-line no-await-in-loop
          loaded.push(...(await service.getSources(adIds.slice(i, i + 50))));
        }
        if (!cancelled) setSources(new Map(loaded.map(s => [s.adId, s])));
      } catch (err) {
        if (!cancelled) setSourcesError(errorText(err));
      } finally {
        if (!cancelled) setSourcesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adIds, service]);

  // Any edit invalidates the last check: a green tick must describe what would
  // actually be sent.
  const updateGroups = (next: ReviewGroup[]) => {
    setGroups(next);
    setResults({});
    setError('');
  };

  const adStatus: AdStatus = launchActive ? 'ACTIVE' : 'PAUSED';
  const blocked = groups.some(
    g => groupProblems(g, geos.get(g.geo)).length > 0,
  );
  const totalAds = groups.reduce((n, g) => n + g.ads.length, 0);

  const check = async () => {
    setChecking(true);
    setError('');
    try {
      const res = await service.check(
        groups.map(g => toBatchRequest(g, adStatus, winners, actor || null)),
      );
      setResults(Object.fromEntries(res.map(r => [groups[r.index].key, r])));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setChecking(false);
    }
  };

  const launch = async () => {
    setLaunching(true);
    setError('');
    try {
      onLaunched(
        await service.launch(
          groups.map(g => toBatchRequest(g, adStatus, winners, actor || null)),
        ),
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLaunching(false);
    }
  };

  return (
    // zIndex 1300 to paint over the fixed app bar, as PreviewDrawer does.
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1300 }}
      PaperProps={{ sx: { width: { xs: '100%', md: 820 } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="center" sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Review geo launch
          </Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
          Each card is one ad set. Winners heading to the same geo campaign
          share an ad set by default; move any ad to its own. Images and videos
          are copied from the US ads as they are — only the landing page,
          tracking and any copy you edit change.
        </Typography>

        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
          {sourcesLoading && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Reading the US ads' copy…
              </Typography>
            </Stack>
          )}
          {sourcesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sourcesError}
            </Alert>
          )}
          <Stack spacing={2}>
            {GEOS.filter(g => groups.some(x => x.geo === g)).map(geo => {
              const info = geos.get(geo);
              const geoGroups = groups.filter(g => g.geo === geo);
              return (
                <Stack key={geo} spacing={1.5}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {info?.name || geo}{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      ({info?.currency})
                    </Typography>
                  </Typography>
                  {geoGroups.map((group, i) => (
                    <GroupCard
                      key={group.key}
                      group={group}
                      index={i}
                      info={info}
                      groups={groups}
                      sources={sources}
                      winners={winners}
                      result={results[group.key]}
                      onChange={next =>
                        updateGroups(
                          groups.map(g => (g.key === next.key ? next : g)),
                        )
                      }
                      onGroups={updateGroups}
                    />
                  ))}
                </Stack>
              );
            })}
          </Stack>
        </Box>

        <Divider />
        <Stack spacing={1} sx={{ p: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={launchActive}
                  onChange={e => setLaunchActive(e.target.checked)}
                />
              }
              label={
                launchActive ? 'Ads go live immediately' : 'Ads start paused'
              }
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              onClick={check}
              disabled={checking || launching || blocked || sourcesLoading}
            >
              {checking ? <CircularProgress size={16} /> : 'Check'}
            </Button>
            <Button
              variant="contained"
              onClick={launch}
              disabled={
                launching || checking || blocked || sourcesLoading || !totalAds
              }
            >
              {launching ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                `Launch ${totalAds} ad${totalAds === 1 ? '' : 's'} into ${groups.length} ad set${groups.length === 1 ? '' : 's'}`
              )}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
}
